<script setup lang="ts">
  import { MpLoadingContainer } from '@borg/ui';

  const userStore = useUserStore();
  const route = useRoute();
  const jobId = route.params.jobId?.toString();
  const source = route.query.source?.toString() ?? 'no_slug';

  const { data: job, pending, status } = await jobRepository.get(jobId);

  const organizationCard = useOrganizationCard.provide();

  const organization = computed(() => {
    if (job.value) {
      if (job.value.organization === 'incognito') {
        return job.value.organization;
      } else {
        return {
          ...job.value.organization,
          ...job.value.employer,
          name: job.value.organization.name,
        };
      }
    }
  });
  watch(
    organization,
    (value) => {
      if (value) {
        organizationCard.set(value);
      }
    },
    { immediate: true },
  );

  const tags = getJobMetaTags();
  useHead(tags.head);
  useSeoMeta(tags.seo);

  async function handleExternalDetails() {
    const getRouteBaseName = useRouteBaseName();
    const routeBaseName = getRouteBaseName(route);

    if (
      job.value &&
      job.value.externalDetailsUrl &&
      job.value.externalDetailsUrl.length &&
      routeBaseName !== 'job-jobId-slug-apply'
    ) {
      await navigate(job.value.externalDetailsUrl, {
        external: true,
        replace: true,
        translate: false,
      });
      return true;
    }
    return false;
  }

  async function fixSlug() {
    const route = useRoute();
    const routeSlug = (route.params.slug?.toString() ?? '').toLowerCase();
    const jobSlug = (job.value?.slug ?? '').toLowerCase();

    if (job.value && routeSlug !== jobSlug) {
      const getRouteBaseName = useRouteBaseName();
      const routeBaseName = getRouteBaseName(route);
      logger.info(
        `Fix job ${jobId} slug ${route.params.slug} ==> ${job.value.slug} on ${routeBaseName}`,
      );
      if (routeBaseName === 'job-jobId' || routeBaseName === 'job-jobId-slug-details') {
        await navigateToJob(
          job.value.id,
          job.value.slug,
          { source, ...route.query },
          { replace: true },
        );
      } else if (routeBaseName === 'job-jobId-slug-apply') {
        await navigateToJobApplication(
          job.value.id,
          job.value.slug,
          { source, ...route.query },
          { replace: true },
        );
      } else if (
        routeBaseName === 'job-jobId-slug-apply-external-countdown' ||
        routeBaseName === 'job-jobId-slug-apply-external-form' ||
        routeBaseName === 'job-jobId-slug-apply-external'
      ) {
        await navigateToExternalJobApplication(
          job.value.id,
          job.value.slug,
          userStore.isAuthenticated,
          { source, ...route.query },
          {
            external: false,
            open: undefined,
            replace: true,
          },
        );
      }
    }
  }

  watch(
    status,
    async (value) => {
      if (value === 'success') {
        if (!(await handleExternalDetails())) {
          await fixSlug();
        }
      } else if (value === 'error') {
        showError({
          statusCode: 404,
          message: 'Job not found',
        });
      }
    },
    { immediate: true },
  );
</script>

<template>
  <MpLoadingContainer :is-loading="pending">
    <NuxtPage />
  </MpLoadingContainer>
</template>
